import { SimpleGrid, Text, VStack, useToast } from '@chakra-ui/react';
import { useState } from 'react';

import { useLEAccordionItemContext } from '../../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { createErrorToast } from '../../../../../utils/toast';
import PhotoLightbox from '../photos/components/PhotoLightbox';
import {
  ADD_MORE_LATER_COPY,
  DRIVERS_LICENSE_SLOT,
  PHOTO_INPUT_ACCEPT,
  PRELIST_ODOMETER_SLOT,
  PRELIST_PHOTO_SLOT,
} from '../photos/constants';
import { usePhotoUpload } from '../photos/hooks/usePhotoUpload';
import PrelistCaptureColumn from './PrelistCaptureColumn';
import PrelistSubmitFooter from './PrelistSubmitFooter';
import usePrelistLicenseCapture from './usePrelistLicenseCapture';
import usePrelistStepStatus from './usePrelistStepStatus';

const INTRO_COPY =
  "Three quick captures and you're ready to list: one photo of your car, your odometer, and your driver's license.";

const SLOT_CAPTIONS: Record<string, string> = {
  [PRELIST_PHOTO_SLOT.id]: 'A clear first look for buyers',
  [PRELIST_ODOMETER_SLOT.id]: 'Your current mileage, right off the dash',
  [DRIVERS_LICENSE_SLOT.id]: 'Front only',
};

const MISSING_LABELS: Record<string, string> = {
  [PRELIST_PHOTO_SLOT.id]: 'car photo',
  [PRELIST_ODOMETER_SLOT.id]: 'odometer',
  [DRIVERS_LICENSE_SLOT.id]: 'license',
};

type MarketplacePrelistPhotosAndIdStepProps = {
  dealId: string;
  isLast: boolean;
};

const MarketplacePrelistPhotosAndIdStep = ({
  dealId,
  isLast,
}: MarketplacePrelistPhotosAndIdStepProps) => {
  const { setOpenAccordionIndex, index } = useLEAccordionItemContext();
  const {
    photos,
    processingSlotIds,
    fileInputRef,
    handleSlotClick,
    handleFileSelected,
    handleFileDrop,
    handleDelete,
    handleReplace,
  } = usePhotoUpload(dealId, { enforceMinimumPhoto: false });
  const license = usePrelistLicenseCapture(dealId);
  const [viewSlotId, setViewSlotId] = useState<string | null>(null);
  const toast = useToast();

  const frontLeftPhoto = photos.get(PRELIST_PHOTO_SLOT.id);
  const odometerPhoto = photos.get(PRELIST_ODOMETER_SLOT.id);
  const isComplete = frontLeftPhoto != null && odometerPhoto != null && license.satisfied;
  usePrelistStepStatus(dealId, isComplete);

  const stillNeeded = [
    !frontLeftPhoto && MISSING_LABELS[PRELIST_PHOTO_SLOT.id],
    !odometerPhoto && MISSING_LABELS[PRELIST_ODOMETER_SLOT.id],
    !license.satisfied && MISSING_LABELS[DRIVERS_LICENSE_SLOT.id],
  ]
    .filter(Boolean)
    .join(', ');

  const handleRemovePhoto = async (slotId: string) => {
    await handleDelete(slotId);
    setOpenAccordionIndex(index);
  };

  const handleRemoveLicense = async () => {
    try {
      await license.remove();
    } catch {
      toast(createErrorToast({ errorMessage: 'There was an error removing your license.' }));
    }
    setOpenAccordionIndex(index);
  };

  const photoSlotEntry = (slot: typeof PRELIST_PHOTO_SLOT) => ({
    slot,
    photo: photos.get(slot.id),
    isProcessing: processingSlotIds.has(slot.id),
    locked: false,
    onClick: () => handleSlotClick(slot.id),
    onReplace: () => handleReplace(slot.id),
    onDelete: () => handleRemovePhoto(slot.id),
    onFileDrop: (file: File) => handleFileDrop(slot.id, file),
  });

  const captureSlots = [
    photoSlotEntry(PRELIST_PHOTO_SLOT),
    photoSlotEntry(PRELIST_ODOMETER_SLOT),
    {
      slot: DRIVERS_LICENSE_SLOT,
      photo: license.photo,
      isProcessing: license.busy,
      locked: license.locked,
      onClick: license.openPicker,
      onReplace: license.openPicker,
      onDelete: handleRemoveLicense,
      onFileDrop: license.upload,
    },
  ];

  const viewedCapture = captureSlots.find(({ slot }) => slot.id === viewSlotId);

  return (
    <VStack spacing={5} align="stretch">
      <Text textAlign="center">{INTRO_COPY}</Text>

      <SimpleGrid columns={3} spacing={5} mx="auto" w="full">
        {captureSlots.map((capture) => (
          <PrelistCaptureColumn
            key={capture.slot.id}
            slot={capture.slot}
            photo={capture.photo}
            isProcessing={capture.isProcessing}
            caption={SLOT_CAPTIONS[capture.slot.id]}
            locked={capture.locked}
            onClick={capture.onClick}
            onView={() => setViewSlotId(capture.slot.id)}
            onDelete={capture.onDelete}
            onFileDrop={capture.onFileDrop}
          />
        ))}
      </SimpleGrid>

      <Text fontSize="xs" color="grayLight" textAlign="center">
        {ADD_MORE_LATER_COPY}
      </Text>

      <PrelistSubmitFooter
        dealId={dealId}
        isLast={isLast}
        isComplete={isComplete}
        isBusy={processingSlotIds.size > 0 || license.busy}
        helperText={isComplete ? undefined : `Still needed: ${stillNeeded}`}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        hidden
        onChange={handleFileSelected}
      />
      <input
        ref={license.inputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        hidden
        onChange={license.onFileSelected}
      />

      <PhotoLightbox
        isOpen={viewedCapture != null}
        onClose={() => setViewSlotId(null)}
        photo={viewedCapture?.photo}
        slotLabel={viewedCapture?.slot.label ?? ''}
        onReplace={() => viewedCapture?.onReplace()}
        onDelete={() => viewedCapture?.onDelete()}
      />
    </VStack>
  );
};

export default MarketplacePrelistPhotosAndIdStep;
